/* =====================================================================================
   Arena and Circus.

   CIRCUS is competitive intelligence, and it is open to the whole company. Knowing who else
   is in the market is not sensitive; it is the thing every commercial conversation needs.

   ARENA is the go-to-market strategy, and it is not open. Editing what the company intends to
   do is deliberately restricted, with two ways through: ask for access, or raise a suggestion
   without needing any. The second is the more useful of the two and is open to everyone,
   because restricting who may EDIT a strategy is sensible and restricting who may point out
   that it is wrong is not.

   THE AI DRAFT IS A DRAFT. Anything generated stays flagged until a person edits or confirms
   it. A board paper quoting an unreviewed machine draft as company strategy is the failure
   this distinction exists to prevent, so it is visible on the page rather than implied.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

/* Both sections live in `atlas`, which is Market, alongside the countries, centres and
   pathways they are about.

   THEY BRIEFLY DID NOT, and that is why this is a named constant rather than a string repeated
   at fourteen call sites. They were created in a schema of their own, which PostgREST will not
   answer for unless the schema is on a project-level exposed list that a migration cannot
   change. Every query returned "Invalid schema: arena" — and because the Arena permission
   check is itself a query, and a failed check reads as a refusal, the owner of the company was
   told he had no access to his own strategy. One constant, one place to change. */
const M = 'atlas';

/* ------------------------------------------------------------------ Circus */

export interface Competitor {
  id: string;
  name: string; short_name: string | null; parent_name: string | null;
  country: string | null; url: string | null;
  /** What they are in this market, which is not the same as what they make, and which moves. */
  model: 'kit' | 'service' | 'both' | 'platform' | 'distributor' | 'unknown';
  ownership: string | null; scale: string | null;
  threat: 'primary' | 'significant' | 'watch' | 'peripheral' | 'unknown';
  our_position: string | null;
  their_strength: string | null; their_weakness: string | null;
  notes: string | null;
  source_name: string; source_url: string | null;
  confidence: string; observed_on: string; active: boolean;
  offerings?: Offering[];
}

export interface Offering {
  id: string; competitor_id: string; category: string;
  product_name: string | null; model: string | null;
  regulatory: string | null; territories: string | null; launched: string | null;
  notes: string | null; source_name: string; source_url: string | null; confidence: string;
}

export interface CompetitorEvent {
  id: string; competitor_id: string | null; counterparty: string | null;
  event_type: string; headline: string; detail: string | null;
  occurred_on: string; value_amount: number | null; value_currency: string | null;
  so_what: string | null; source_name: string; source_url: string | null;
}

/** Footprint assembled from what the field records against centres, not maintained separately. */
export interface Presence {
  competitor_id: string | null; competitor: string | null; recorded_as: string;
  /** True where the match came from an alias, so the page can show both names. */
  matched_by_alias?: boolean;
  sites: number; countries: number; country_list: string[];
  method_records: number; known_annual_value: number | null;
  marked_displaceable: number; earliest_window: string | null;
}

/* What a competitor sells, category by category, in the tenant's own words. This list was
   hardcoded here as the transplant diagnostics set, so a pharmaceutical tenant's Circus
   captioned its competitors' drugs "Donor-derived cfDNA": one tenant's industry shipped to
   every tenant as if it were the product's. The tenant declares its own, exactly as it
   declares its segments and site kinds; nothing in this file knows what any category means. */
export const CATEGORIES: readonly { key: string; label: string }[] =
  TENANT.vocabulary.offeringCategories;

/* What a competitor sells. The full list mirrors the CHECK on atlas.competitors.model since
   0084 widened it beyond diagnostics; the filter chips in Circus show only the models present
   in the tenant's own competitor set, so a pharmaceutical company is not offered "Kits". */
export const MODELS = [
  { key: 'drug', label: 'Drug', note: 'A small-molecule medicine' },
  { key: 'biologic', label: 'Biologic', note: 'A biological medicine' },
  { key: 'cell_therapy', label: 'Cell therapy', note: 'Cell or gene therapy' },
  { key: 'combination', label: 'Combination', note: 'Drug and device, or several actives' },
  { key: 'kit', label: 'Kits', note: 'IVD products a laboratory runs itself' },
  { key: 'service', label: 'Service', note: 'Samples sent to their laboratory' },
  { key: 'both', label: 'Both', note: 'Kits and a service' },
  { key: 'platform', label: 'Platform', note: 'Instruments others run assays on' },
  { key: 'device', label: 'Device', note: 'A medical device' },
  { key: 'software', label: 'Software', note: 'Software, including software as a device' },
  { key: 'distributor', label: 'Distributor', note: 'Sells someone else’s products' },
  { key: 'unknown', label: 'Not established', note: '' },
] as const;

/* A competitor is one company; what we think of it is per market. With a market selected the
   read comes from atlas.v_competitors_by_market, which resolves the assessment to that market
   and falls back to the company-level fields where a tenant never set one. With no market
   selected, or a tenant that has only one, the plain table is exactly what it always was, so
   nothing changes for a single-market tenant. */
export async function fetchCompetitors(marketId?: string | null): Promise<Competitor[]> {
  if (!live()) return (TENANT.seed.competitor ?? []) as Competitor[];
  const { supabase } = await import('./supabase');
  if (marketId) {
    const { data, error } = await supabase().schema(M)
      .from('v_competitors_by_market').select('*')
      .eq('market_id', marketId).order('threat').order('name');
    if (error) throw new Error(explain(error.message, 'load the competitor list'));
    // The view keys on competitor_id; everything downstream expects id.
    return ((data ?? []) as unknown as (Competitor & { competitor_id: string })[])
      .map((r) => ({ ...r, id: r.competitor_id })) as Competitor[];
  }
  const { data, error } = await supabase().schema(M)
    .from('competitors').select('*').eq('active', true).order('threat').order('name');
  if (error) throw new Error(explain(error.message, 'load the competitor list'));
  return (data ?? []) as Competitor[];
}

export async function fetchOfferings(): Promise<Offering[]> {
  if (!live()) return (TENANT.seed.offering ?? []) as Offering[];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('competitor_offerings').select('*').order('category');
  if (error) throw new Error(explain(error.message, 'load competitor products'));
  return (data ?? []) as Offering[];
}

export async function fetchCompetitorEvents(): Promise<CompetitorEvent[]> {
  if (!live()) return (TENANT.seed.competitorEvent ?? []) as CompetitorEvent[];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('competitor_events').select('*').order('occurred_on', { ascending: false }).limit(60);
  if (error) throw new Error(explain(error.message, 'load market events'));
  return (data ?? []) as CompetitorEvent[];
}

/** Where competitors actually are, derived from centre incumbents and per-method vendors. */
export async function fetchPresence(): Promise<Presence[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('v_competitor_presence').select('*').order('sites', { ascending: false });
  if (error) throw new Error(explain(error.message, 'load the competitor footprint'));
  return (data ?? []) as Presence[];
}

/* Attach a name an existing competitor is already known by.

   The alternative offered on this panel used to be "Add them", and for GenDx or IMDx that is
   the wrong answer: both are already in Circus under the name of the group that owns them, and
   creating a second record would split one company's footprint in two and understate the
   incumbent everywhere it appears. The database refuses an alias another competitor already
   answers to, for the same reason. */
export async function linkVendorAlias(competitorId: string, alias: string): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .rpc('link_vendor_alias', { p_competitor_id: competitorId, p_alias: alias });
  if (error) throw new Error(explain(error.message, 'link this name'));
  return String(data ?? 'Linked');
}

export async function saveCompetitor(c: Partial<Competitor>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema(M).from('competitors')
    .upsert(c, { onConflict: 'id' });
  if (error) throw new Error(explain(error.message, 'save this competitor'));
}

/* ------------------------------------------------------------------ Arena */

export interface Strategy {
  id: string; title: string; horizon: string; status: string;
  summary: string | null;
  /** True until a person has touched it. Shown on the page, not implied. */
  ai_drafted: boolean; drafted_at: string | null; draft_basis: Record<string, unknown> | null;
  agreed_at: string | null; created_at: string;
}

export interface Move {
  id: string; strategy_id: string; rank: number; title: string; rationale: string | null;
  market_iso3: string | null; pathway_id: string | null; site_id: string | null;
  competitor_id: string | null;
  effort: string | null; confidence: string | null; time_horizon: string | null;
  blocked_by: string | null; owner_id: string | null;
  status: string; decision_note: string | null;
  ai_drafted: boolean; edited_by: string | null; edited_at: string | null;
}

export interface Suggestion {
  id: string; strategy_id: string | null; move_id: string | null;
  body: string; kind: string; evidence_url: string | null;
  raised_by: string | null; raised_at: string;
  status: string; response: string | null;
}

export async function mayReadArena(): Promise<boolean> {
  if (!live()) return true;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M).rpc('arena_may_read');
  if (error) return false;
  return Boolean(data);
}

export async function mayEditArena(): Promise<boolean> {
  if (!live()) return true;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M).rpc('arena_may_edit');
  if (error) return false;
  return Boolean(data);
}

export async function fetchStrategies(): Promise<Strategy[]> {
  if (!live()) return (TENANT.seed.strategy ?? []) as Strategy[];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('strategies').select('*').order('created_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'load the strategy'));
  return (data ?? []) as Strategy[];
}

export async function fetchMoves(strategyId: string): Promise<Move[]> {
  if (!live()) return ((TENANT.seed.move ?? []) as Move[]).filter((m) => m.strategy_id === strategyId);
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('strategy_moves').select('*').eq('strategy_id', strategyId).order('rank');
  if (error) throw new Error(explain(error.message, 'load the strategy'));
  return (data ?? []) as Move[];
}

/** Editing clears the machine-drafted flag: what a person has put their name to must be
    distinguishable from what a model proposed. */
export async function saveMove(m: Partial<Move>) {
  const { supabase } = await import('./supabase');
  const { currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema(M).from('strategy_moves')
    .upsert({ ...m, ai_drafted: false, edited_by: me?.id, edited_at: new Date().toISOString() },
            { onConflict: 'id' });
  if (error) throw new Error(explain(error.message, 'save this move'));
}

export async function fetchSuggestions(): Promise<Suggestion[]> {
  if (!live()) return (TENANT.seed.suggestion ?? []) as Suggestion[];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema(M)
    .from('arena_suggestions').select('*').order('raised_at', { ascending: false }).limit(50);
  if (error) throw new Error(explain(error.message, 'load suggestions'));
  return (data ?? []) as Suggestion[];
}

/** Open to everyone, deliberately. */
export async function raiseSuggestion(s: {
  strategyId?: string | null; moveId?: string | null; body: string; kind: string;
  evidenceUrl?: string | null;
}) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  const { error } = await supabase().schema(M).from('arena_suggestions').insert({
    org_id: org?.id, strategy_id: s.strategyId ?? null, move_id: s.moveId ?? null,
    body: s.body, kind: s.kind, evidence_url: s.evidenceUrl ?? null, raised_by: me?.id,
  });
  if (error) throw new Error(explain(error.message, 'raise this suggestion'));
}

export async function requestArenaAccess(level: 'read' | 'edit', reason: string) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { data: org } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  const { error } = await supabase().schema(M).from('arena_access_requests').insert({
    org_id: org?.id, member_id: me?.id, level, reason,
  });
  if (error) throw new Error(explain(error.message, 'request access'));
}
